export type QuestionType =
    | 'boolean'
    | 'single-choice'
    | 'multi-choice'
    | 'text'
    | 'number'
    | 'integer'
    | 'float'
    | 'percentage'
    | 'date'
    | 'table';

/**
 * The numeric response types, which all share one editor.
 *
 * The data dictionary distinguishes `integer`/`float`/`percentage` and has no
 * `number`; checklists published before that split still carry `number`, so all
 * four spellings reach us. Matching the family rather than one literal keeps a
 * numeric question off the free-text editor. Mirrors the surveyor app's
 * NUMERIC_TYPES.
 */
const NUMERIC_QUESTION_TYPES = new Set<string>(['number', 'integer', 'float', 'percentage']);

export const isNumericQuestionType = (type?: string | null): boolean =>
    !!type && NUMERIC_QUESTION_TYPES.has(type);

export interface QuestionOption {
    id: string;
    text: string;
}

export interface QuestionValidation {
    maxLength?: number;
    min?: number;
    max?: number;
    /** Fraction digits allowed on a numeric answer, from the data dictionary. */
    decimalPlaces?: number;
    pattern?: string;
}

export interface Question {
    id: string;
    type: QuestionType;
    question: string;
    required?: boolean;
    dataDictionaryId?: string;
    options?: QuestionOption[];
    unit?: string;
    validation?: QuestionValidation;
    dependsOn?: string;
    conditionalQuestions?: Record<string, string[]>;
}

export interface Subsection {
    id: string;
    title: string;
    questions: Question[];
}

export interface Section {
    id: string;
    title: string;
    order: number;
    subsections: Subsection[];
}

export interface ChecklistConfig {
    version: string;
    lastUpdated: string;
    checklistType: string;
    metadata: {
        title: string;
        subtitle: string;
        organization: string;
        documentType: string;
    };
    sections: Section[];
}

export type QuestionMap = Record<string, Question>;

export interface QuestionLocation {
    sectionId: string;
    sectionTitle: string;
    sectionOrder: number;
    subsectionId: string;
    subsectionTitle: string;
}

export type QuestionLocationMap = Record<string, QuestionLocation>;
